/**
 * Organization context facade for the RBAC system.
 *
 * Historically this was a 1,583-line monolith spanning nine domains with zero
 * memoization (audit A1 / item 26). It is now a thin composition over three
 * focused providers:
 *
 *   - `OrgCoreProvider`    memberships / active org, RBAC permissions,
 *                          onboarding + profile, org-creation, trial capture.
 *   - `BillingProvider`    org_billing + grace, trial extension/acknowledge.
 *   - `WorkspacesProvider` workspaces CRUD + the active workspace.
 *
 * `OrgProvider` nests them in dependency order (core -> billing -> workspaces,
 * since the latter two read `activeOrg` from core) and `useOrg()` recombines
 * the three contexts into the legacy `OrgContextType` so every existing call
 * site keeps compiling unchanged. New code can reach for the narrower
 * `useOrgCore` / `useBilling` / `useWorkspaces` hooks directly.
 *
 * The two refresh counters that the legacy provider used as an event bus are
 * gone: `useOrg().refresh()` now calls the explicit per-domain refetches
 * (`refreshCore` + `refreshBilling`).
 *
 * @module lib/OrgProvider
 */

import React, { useCallback, useMemo } from 'react';
import { OrgCoreProvider, useOrgCore } from './OrgCoreProvider';
import { BillingProvider, useBilling } from './BillingProvider';
import { WorkspacesProvider, useWorkspaces } from './WorkspacesProvider';
import type {
  Organization,
  OrgBilling,
  OrgMember,
  OnboardingState,
  UserOrgMembership,
  SetupStep,
  GraceState,
  ActivatedTrial,
  Workspace,
} from './org-types';

// Re-export the error classes so existing `from '../lib/OrgProvider'` imports
// (AcceptInvitePage, OnboardingSplash, CreateOrgStep, tests, etc.) keep working.
export {
  OrgLimitReachedError,
  CreateOrgRetryError,
  CreateOrgError,
} from './OrgCoreProvider';
export {
  TrialNotActiveError,
  TrialAlreadyExtendedError,
  ExtendTrialError,
} from './BillingProvider';

/**
 * Legacy combined organization context shape. Recomposed from the three split
 * providers by `useOrg`. New code should prefer the narrower context hooks.
 */
export interface OrgContextType {
  /** Currently active organization, or null if not yet loaded. */
  activeOrg: Organization | null;
  /** Switch the active organization by ID. */
  setActiveOrg: (orgId: string) => void;
  /** All organizations the user belongs to. */
  userOrgs: Organization[];
  /** All memberships with role info. */
  memberships: UserOrgMembership[];
  /** Billing record for the active org, or null. */
  orgBilling: OrgBilling | null;
  /** Whether the active org's billing row is still being fetched. */
  billingLoading: boolean;
  /** Members of the active org. */
  orgMembers: OrgMember[];
  /** The current user's role name in the active org. */
  userRole: string | null;
  /** Permission keys granted to the active role in the active org. */
  permissions: Set<string>;
  /** Returns whether the active role grants `key`. Fails closed. */
  hasPermission: (key: string) => boolean;
  /** Whether the provider is still loading. */
  isLoading: boolean;
  /** User-facing error message from org initialization, or null. */
  orgError: string | null;
  /** Re-fetch org data (memberships + billing). */
  refresh: () => void;
  /** Current onboarding state. */
  onboardingState: OnboardingState;
  /** Ordered list of setup steps the user must still complete. */
  setupSteps: SetupStep[];
  /** Grace period state for the active org. */
  graceState: GraceState;
  /** Update the current user's first/last name via Supabase auth. */
  updateProfile: (data: { firstName: string; lastName: string }) => Promise<void>;
  /** Create a new organization via the metering proxy. */
  createOrg: (name: string, idempotencyKey?: string) => Promise<Organization>;
  /** Create an organization plus its default workspace. */
  createOrgWithWorkspace: (
    orgName: string,
    workspaceName: string,
    websiteUrl: string,
    idempotencyKey: string,
  ) => Promise<{ orgId: string; workspaceId: string }>;
  /** Complete the onboarding flow: update profile and create org. */
  completeOnboarding: (data: {
    firstName: string;
    lastName: string;
    orgName: string;
  }) => Promise<void>;
  /** Details of a trial that was just activated by `createOrg`, or null. */
  justActivatedTrial: ActivatedTrial | null;
  /** Clear `justActivatedTrial` and bump `billingRefreshKey`. */
  acknowledgeTrial: () => void;
  /** Extend the active trial by 7 days via the metering service. */
  extendTrial: () => Promise<void>;
  /** Monotonic counter bumped when billing-derived server state may have shifted. */
  billingRefreshKey: number;
  /** Workspaces belonging to the active org. */
  workspaces: Workspace[];
  /** Currently active workspace, or null. */
  activeWorkspace: Workspace | null;
  /** Switch the active workspace by id (persisted to localStorage). */
  setActiveWorkspaceId: (id: string) => void;
  /** User-facing error from the most recent workspaces fetch, or null. */
  workspacesError: string | null;
  /** Whether the most recent workspaces fetch is in flight. */
  workspacesLoading: boolean;
  /** Re-run the workspaces fetch for the active org. */
  refreshWorkspaces: () => Promise<void>;
  /** Re-fetch only the active workspace's row by id. */
  refreshActiveWorkspace: () => Promise<Workspace | null>;
  /** Create a new workspace inside the active org. */
  createWorkspace: (name: string, websiteUrl: string | null) => Promise<Workspace>;
}

/** Props for the OrgProvider component. */
interface OrgProviderProps {
  children: React.ReactNode;
}

/**
 * Composes the three split org providers in dependency order. Must be a child
 * of `SupabaseProvider`. Billing and workspaces nest inside core because they
 * read the active org from it.
 */
export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => (
  <OrgCoreProvider>
    <BillingProvider>
      <WorkspacesProvider>{children}</WorkspacesProvider>
    </BillingProvider>
  </OrgCoreProvider>
);

/**
 * Access the combined organization context. Recombines the three split
 * contexts into the legacy `OrgContextType` so existing call sites keep
 * working. Must be called within an `OrgProvider`.
 *
 * @throws Error if called outside the provider tree.
 */
export const useOrg = (): OrgContextType => {
  const core = useOrgCore();
  const billing = useBilling();
  const workspaces = useWorkspaces();

  /**
   * Legacy combined refresh: re-fetch core (memberships + active org),
   * billing, and workspaces. Replaces the monolith's `setRefreshKey` event-bus
   * bump. Workspaces were previously omitted, leaving the workspace list stale
   * after an external org change until the next navigation.
   */
  const refresh = useCallback(() => {
    core.refreshCore();
    void billing.refreshBilling();
    void workspaces.refreshWorkspaces();
  }, [core, billing, workspaces]);

  return useMemo<OrgContextType>(
    () => ({
      // Core
      activeOrg: core.activeOrg,
      setActiveOrg: core.setActiveOrg,
      userOrgs: core.userOrgs,
      memberships: core.memberships,
      orgMembers: core.orgMembers,
      userRole: core.userRole,
      permissions: core.permissions,
      hasPermission: core.hasPermission,
      isLoading: core.isLoading,
      orgError: core.orgError,
      onboardingState: core.onboardingState,
      setupSteps: core.setupSteps,
      updateProfile: core.updateProfile,
      createOrg: core.createOrg,
      createOrgWithWorkspace: core.createOrgWithWorkspace,
      completeOnboarding: core.completeOnboarding,
      justActivatedTrial: core.justActivatedTrial,
      refresh,
      // Billing
      orgBilling: billing.orgBilling,
      billingLoading: billing.billingLoading,
      graceState: billing.graceState,
      acknowledgeTrial: billing.acknowledgeTrial,
      extendTrial: billing.extendTrial,
      billingRefreshKey: billing.billingRefreshKey,
      // Workspaces
      workspaces: workspaces.workspaces,
      activeWorkspace: workspaces.activeWorkspace,
      setActiveWorkspaceId: workspaces.setActiveWorkspaceId,
      workspacesError: workspaces.workspacesError,
      workspacesLoading: workspaces.workspacesLoading,
      refreshWorkspaces: workspaces.refreshWorkspaces,
      refreshActiveWorkspace: workspaces.refreshActiveWorkspace,
      createWorkspace: workspaces.createWorkspace,
    }),
    [core, billing, workspaces, refresh],
  );
};

export default OrgProvider;
